import { Component, forwardRef, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';

import { CalendarModule } from 'primeng/calendar';
import { FilterMetadata } from 'primeng/api';

import { MatchMode } from '../../../../common/domain';
import { AbstractFilterComponent } from '../../../../common/modules/filters/abstract';
import { DateTime } from 'luxon';

@Component({
	standalone: true,
	selector: 'br-month-filter',
	templateUrl: './month-filter.component.html',
	imports: [FormsModule, CalendarModule],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => MonthFilterComponent),
			multi: true,
		},
	],
})

export class MonthFilterComponent extends AbstractFilterComponent {

	/** The filter this component emits for a month: [first day, last day] as ISO dates */
	static toFilter(month: Date, matchMode: string | MatchMode = MatchMode.Between): FilterMetadata {
		const dt = DateTime.fromJSDate(month);
		return {
			matchMode,
			value: [dt.startOf('month').toISODate(), dt.endOf('month').toISODate()],
		};
	}

	@Input()
	label: string = $localize`Select month`;

	@Input()
	icon: string;

	@Input()
	required: boolean = false;

	@Input()
	maxDate = new Date();

	@Input()
	minDate = new Date(1900, 0, 1);

	@Input()
	showIcon: boolean = true;

	@Input()
	showButtonBar: boolean = false;

	@Input()
	name: string;

	@Input()
	override matchMode: string | MatchMode = MatchMode.Between;

	override writeValue(model: FilterMetadata): void {
		super.writeValue({ ...model, value: this.toDate(model?.value) });
	}

	protected override onChangeHandler(value: Date) {
		this.model.value = value;
		if (!value) {
			this.onModelChange(null);
			return;
		}

		this.onModelChange(MonthFilterComponent.toFilter(value, this.matchMode));
	}

	private toDate(value: unknown): Date | null {
		const raw: unknown = Array.isArray(value) ? value[0] : value;
		if (raw instanceof Date) {
			return raw;
		}
		if (typeof raw !== 'string' || !raw) {
			return null;
		}
		const dt = DateTime.fromISO(raw);
		return dt.isValid ? dt.toJSDate() : null;
	}

}
